/**
 * MissingDocumentsFilters
 *
 * Search, Provider (multi), Form Type (when only Forms is selected), and Types (multi).
 */

import { ActionIcon, Group, Select, Tooltip } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { SearchInput } from '../../../components/SearchInput';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import type { MissingDocumentFilters as MissingDocumentFiltersType } from '../types';
import {
  MissingDocumentCategoryLabels,
  MissingDocumentFormTypeLabels,
} from '../types';

interface MissingDocumentsFiltersProps {
  filters: MissingDocumentFiltersType;
  onFiltersChange: (f: MissingDocumentFiltersType) => void;
  providerOptions: { value: string; label: string }[];
}

const typeOptions = Object.entries(MissingDocumentCategoryLabels).map(([value, label]) => ({ value, label }));

const formTypeOptions = [
  { value: 'all', label: 'All form types' },
  ...Object.entries(MissingDocumentFormTypeLabels).map(([value, label]) => ({ value, label })),
];

export function MissingDocumentsFilters({
  filters,
  onFiltersChange,
  providerOptions,
}: MissingDocumentsFiltersProps): JSX.Element {
  const resetFilters = (): void => {
    onFiltersChange({
      ...filters,
      search: '',
      providerIds: [],
      types: [],
      formType: 'all',
    });
  };

  const hasActiveFilters =
    filters.search !== '' ||
    filters.providerIds.length > 0 ||
    filters.types.length > 0 ||
    filters.formType !== 'all';

  /** Form Type select only when only "Forms" is selected in Types. */
  const showFormTypeFilter =
    filters.types.length === 1 && filters.types[0] === 'forms';

  return (
    <Group gap="md" wrap="wrap" align="center" flex="1" style={{ width: '100%' }}>
      <SearchInput
        placeholder="Search documents..."
        iconSize={14}
        value={filters.search}
        onChange={(e) => onFiltersChange({ ...filters, search: e.currentTarget.value })}
        w={280}
      />

      <FilterMultiSelect
        allLabel="All providers"
        data={providerOptions}
        value={filters.providerIds}
        onChange={(v) => onFiltersChange({ ...filters, providerIds: v })}
        w={220}
      />

      {showFormTypeFilter && (
        <Select
          placeholder="Form type"
          value={filters.formType}
          onChange={(v) =>
            onFiltersChange({
              ...filters,
              formType: (v || 'all') as MissingDocumentFiltersType['formType'],
            })
          }
          data={formTypeOptions}
        />
      )}

      <FilterMultiSelect
        allLabel="All types"
        data={typeOptions}
        value={filters.types}
        onChange={(v) => onFiltersChange({ ...filters, types: v as MissingDocumentFiltersType['types'] })}
        w={220}
      />

      {hasActiveFilters && (
        <Tooltip label="Clear filters">
          <ActionIcon variant="light" color="gray" size="sm" onClick={resetFilters}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}
